import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MutableRefObject,
} from 'react';

import type { PanoramaHotspot } from '../contracts/models';
import { usePannellumHotspots } from '../hooks/usePannellumHotspots';
import {
  buildPannellumHotspotConfig,
  panoramaPositionWithPitchOffset,
  type PanoramaHotspotElementRenderer,
  type PanoramaTarget,
  type PannellumFactory,
  type PannellumViewer,
} from '../panorama/pannellum';

export interface PannellumPanoramaProps {
  sceneId: string | number;
  imageUrl: string;
  hotspots?: readonly PanoramaHotspot[];
  hotspotsEnabled?: boolean;
  target?: PanoramaTarget | null;
  pitchOffset?: number;
  viewerRef?: MutableRefObject<PannellumViewer | null>;
  factory?: PannellumFactory | null;
  className?: string;
  style?: CSSProperties;
  containerId?: string;
  hotspotClassName?: string;
  renderHotspotElement?: PanoramaHotspotElementRenderer;
  onHotspotClick?: (hotspot: PanoramaHotspot) => void;
  onTargetReached?: () => void;
  onLoad?: (viewer: PannellumViewer) => void;
  onError?: (error: Error) => void;
  suppressErrorMessage?: boolean;
  watchdogIntervalMs?: number;
}

const getGlobalPannellumFactory = (): PannellumFactory | null => {
  const candidate = (globalThis as { pannellum?: PannellumFactory }).pannellum;
  return candidate ?? null;
};

const ORIENTATION_SESSION_KEY =
  '@ironview/interactive-viewer:orientation-enabled';
let orientationEnabledInMemory = false;

const getSessionOrientationPreference = (): boolean => {
  try {
    const storedPreference = globalThis.sessionStorage?.getItem(
      ORIENTATION_SESSION_KEY,
    );
    if (storedPreference !== null && storedPreference !== undefined) {
      orientationEnabledInMemory = storedPreference === 'true';
    }
  } catch {
    // The in-memory fallback still preserves the preference when storage is
    // blocked by the browser.
  }
  return orientationEnabledInMemory;
};

const setSessionOrientationPreference = (enabled: boolean): void => {
  orientationEnabledInMemory = enabled;
  try {
    globalThis.sessionStorage?.setItem(
      ORIENTATION_SESSION_KEY,
      String(enabled),
    );
  } catch {
    // Session storage is best-effort; the module-level value remains usable.
  }
};

export const PannellumPanorama = ({
  sceneId,
  imageUrl,
  hotspots = [],
  hotspotsEnabled = true,
  target,
  pitchOffset = 0,
  viewerRef: providedViewerRef,
  factory,
  className = 'iv-panorama__container',
  style,
  containerId,
  hotspotClassName,
  renderHotspotElement,
  onHotspotClick,
  onTargetReached,
  onLoad,
  onError,
  suppressErrorMessage = false,
  watchdogIntervalMs,
}: PannellumPanoramaProps) => {
  const internalViewerRef = useRef<PannellumViewer | null>(null);
  const viewerRef = providedViewerRef ?? internalViewerRef;
  const containerRef = useRef<HTMLDivElement>(null);
  const orientationWasActiveRef = useRef(getSessionOrientationPreference());
  const hotspotsRef = useRef(hotspots);
  hotspotsRef.current = hotspots;
  const runtimeOptionsRef = useRef({
    hotspotsEnabled,
    hotspotClassName,
    suppressErrorMessage,
  });
  runtimeOptionsRef.current = {
    hotspotsEnabled,
    hotspotClassName,
    suppressErrorMessage,
  };
  const callbacksRef = useRef({
    onHotspotClick,
    onTargetReached,
    onLoad,
    onError,
    renderHotspotElement,
  });
  callbacksRef.current = {
    onHotspotClick,
    onTargetReached,
    onLoad,
    onError,
    renderHotspotElement,
  };
  const recoveredViewRef = useRef<{
    sceneId: string | number; imageUrl: string; pitch: number; yaw: number; hfov: number;
  } | null>(null);
  const contextLostRef = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [recoveryRevision, setRecoveryRevision] = useState(0);

  const destroyViewer = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    try {
      const orientationWasActive = viewer.isOrientationActive();
      orientationWasActiveRef.current = orientationWasActive;
      setSessionOrientationPreference(orientationWasActive);
    } catch {
      // Preserve the last known session preference if Pannellum has already
      // released its orientation state.
    }
    try {
      viewer.destroy();
    } catch {
      // Pannellum may already have released its WebGL context.
    }
    viewerRef.current = null;
    setLoaded(false);
  }, [viewerRef]);

  usePannellumHotspots({
    viewerRef,
    containerRef,
    hotspots,
    enabled: hotspotsEnabled && loaded,
    className: hotspotClassName,
    renderElement: renderHotspotElement,
    onHotspotClick,
    watchdogIntervalMs,
  });

  useEffect(() => {
    if (!imageUrl) return;

    const timeoutId = window.setTimeout(() => {
      const container = containerRef.current;
      const pannellumFactory = factory ?? getGlobalPannellumFactory();
      if (!container || !pannellumFactory) {
        callbacksRef.current.onError?.(
          new Error('Pannellum factory or panorama container is unavailable'),
        );
        return;
      }

      destroyViewer();
      container.replaceChildren();

      try {
        const initialHotspots = runtimeOptionsRef.current.hotspotsEnabled
          ? hotspotsRef.current.map((hotspot) =>
              buildPannellumHotspotConfig(hotspot, {
                className: runtimeOptionsRef.current.hotspotClassName,
                renderElement: callbacksRef.current.renderHotspotElement,
                onClick: (clicked) =>
                  callbacksRef.current.onHotspotClick?.(clicked),
              }),
            )
          : [];
        const savedView = recoveredViewRef.current;
        const restoreView = savedView?.sceneId === sceneId && savedView.imageUrl === imageUrl
          ? savedView : null;
        recoveredViewRef.current = null;
        contextLostRef.current = false;
        const viewer = pannellumFactory.viewer(container, {
          type: 'equirectangular',
          panorama: imageUrl,
          autoLoad: true,
          showControls: true,
          showZoomCtrl: false,
          showFullscreenCtrl: false,
          hotSpotDebug: false,
          pitch: restoreView?.pitch ?? 0,
          yaw: restoreView?.yaw ?? 0,
          hfov: restoreView?.hfov ?? 100,
          hotSpots: initialHotspots,
        });
        viewerRef.current = viewer;

        viewer.on('load', () => {
          setLoaded(true);
          window.setTimeout(() => viewerRef.current?.resize(), 100);
          if (
            orientationWasActiveRef.current &&
            viewer.isOrientationSupported()
          ) {
            viewer.startOrientation();
          }

          callbacksRef.current.onLoad?.(viewer);
        });
        viewer.on('error', (error) => {
          if (runtimeOptionsRef.current.suppressErrorMessage) {
            const errorMessage =
              container.querySelector<HTMLElement>('.pnlm-error-msg');
            if (errorMessage) {
              errorMessage.style.setProperty('display', 'none', 'important');
              errorMessage.setAttribute('aria-hidden', 'true');
            }
          }

          callbacksRef.current.onError?.(
            error instanceof Error ? error : new Error(String(error)),
          );
        });
      } catch (error) {
        callbacksRef.current.onError?.(
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    }, 100);

    return () => window.clearTimeout(timeoutId);
  }, [
    destroyViewer,
    factory,
    imageUrl,
    recoveryRevision,
    sceneId,
    viewerRef,
  ]);

  useEffect(() => () => destroyViewer(), [destroyViewer, sceneId]);

  useEffect(() => {
    if (!loaded || !target || !viewerRef.current) return;
    const adjustedTarget = panoramaPositionWithPitchOffset(
      target,
      pitchOffset,
    );
    viewerRef.current.lookAt(
      adjustedTarget.pitch,
      adjustedTarget.yaw,
      undefined,
      target.duration ?? 1000,
    );
    callbacksRef.current.onTargetReached?.();
  }, [
    loaded,
    pitchOffset,
    target?.duration,
    target?.pitch,
    target?.yaw,
    viewerRef,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let resumeFrame: number | null = null;
    const resume = () => {
      if (document.hidden || !viewerRef.current) return;
      const viewer = viewerRef.current;
      try {
        const canvas = container.querySelector('canvas');
        const gl = canvas?.getContext('webgl') ?? canvas?.getContext('webgl2');
        if (contextLostRef.current || gl?.isContextLost()) {
          // Rebuild only the lost renderer, preserving where the user was looking.
          recoveredViewRef.current = {
            sceneId, imageUrl,
            pitch: viewer.getPitch(), yaw: viewer.getYaw(), hfov: viewer.getHfov(),
          };
          destroyViewer();
          setRecoveryRevision((revision) => revision + 1);
        } else {
          viewer.resize();
        }
      } catch {
        // Browser canvas inspection is best-effort.
      }
    };
    const scheduleResume = () => {
      if (resumeFrame !== null) cancelAnimationFrame(resumeFrame);
      resumeFrame = requestAnimationFrame(() => {
        resumeFrame = null;
        resume();
      });
    };
    const onContextLost = (event: Event) => {
      event.preventDefault();
      contextLostRef.current = true;
      scheduleResume();
    };
    container.addEventListener('webglcontextlost', onContextLost, true);
    document.addEventListener('visibilitychange', scheduleResume);
    window.addEventListener('pageshow', scheduleResume);
    window.addEventListener('focus', scheduleResume);
    return () => {
      if (resumeFrame !== null) cancelAnimationFrame(resumeFrame);
      container.removeEventListener('webglcontextlost', onContextLost, true);
      document.removeEventListener('visibilitychange', scheduleResume);
      window.removeEventListener('pageshow', scheduleResume);
      window.removeEventListener('focus', scheduleResume);
    };
  }, [destroyViewer, viewerRef, sceneId, imageUrl]);

  return (
    <div
      ref={containerRef}
      id={containerId}
      className={className}
      style={{ backgroundColor: '#000', ...style }}
      data-tour-id="iv-panorama-viewer"
    />
  );
};
